import type { CompareTitleParts } from '../types/compare.types'
import type { ElitePlansContentSource, LandingContent } from '../types/landingContent.types'
import type { PlanConfig } from '../types/plan.types'
import type { TestimonialsTitle } from '../types/testimonials.types'
import { localLandingContent } from './localLandingContent'
import { resolveThemeId } from '../planTheme'

export const localElitePlansContentSource: ElitePlansContentSource = {
  load: async () => localLandingContent
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isValidPlanConfig(value: unknown): value is PlanConfig {
  if (!isRecord(value)) return false
  if (typeof value.id !== 'string' || !value.id) return false
  if (typeof value.tabLabel !== 'string' || !value.tabLabel) return false
  if (!isRecord(value.hero)) return false
  if (typeof value.hero.headlineSans !== 'string') return false
  if (typeof value.hero.headlineSerif !== 'string') return false
  if (typeof value.hero.subcopy !== 'string') return false
  if (!isRecord(value.cta) || typeof value.cta.label !== 'string') return false
  return true
}

function mergePlan(local: PlanConfig, remote: PlanConfig): PlanConfig {
  return {
    ...local,
    ...remote,
    themeId: resolveThemeId(remote.themeId ?? local.themeId),
    hero: { ...local.hero, ...remote.hero },
    trustItems: remote.trustItems?.length ? remote.trustItems : local.trustItems,
    cta: { ...local.cta, ...remote.cta },
    consentTextList: remote.consentTextList ?? local.consentTextList,
    lockInPriceBanner: remote.lockInPriceBanner ?? local.lockInPriceBanner,
    plusBanner: remote.plusBanner ?? local.plusBanner,
    allocationPreview: remote.allocationPreview ?? local.allocationPreview,
    insightBanner: remote.insightBanner ?? local.insightBanner,
    // Advice card *set* is product catalog (see adviceCardCatalog) — CMS may only overlay chrome by id
    adviceCards: local.adviceCards,
    featuresTitle: remote.featuresTitle ? { ...local.featuresTitle, ...remote.featuresTitle } : local.featuresTitle,
    features: remote.features?.length ? remote.features : local.features,
    featuresAfterCompare: remote.featuresAfterCompare ?? local.featuresAfterCompare,
    testimonialsTitle: toTestimonialsTitle(remote.testimonialsTitle, local.testimonialsTitle),
    testimonials: remote.testimonials?.length ? remote.testimonials : local.testimonials
  }
}

/** Maps a future CMS payload onto LandingContent. Returns null when unusable. */
export function mapCrmLandingContent(payload: unknown): LandingContent | null {
  if (!isRecord(payload)) return null
  if (!Array.isArray(payload.planOrder) || !isRecord(payload.plans)) return null

  const plans: Record<string, PlanConfig> = {}
  for (const [id, plan] of Object.entries(payload.plans)) {
    if (!isValidPlanConfig(plan)) continue
    plans[id] = {
      ...plan,
      id: plan.id || id,
      themeId: resolveThemeId(plan.themeId)
    }
  }

  const planOrder = (payload.planOrder as unknown[]).filter(
    (id): id is string => typeof id === 'string' && Boolean(plans[id])
  )

  if (planOrder.length === 0) return null

  return {
    contentVersion: typeof payload.contentVersion === 'string' ? payload.contentVersion : 'crm',
    planOrder,
    plans,
    compareTitle: toCompareTitle(payload.compareTitle, localLandingContent.compareTitle),
    compareSections: Array.isArray(payload.compareSections)
      ? (payload.compareSections as LandingContent['compareSections'])
      : localLandingContent.compareSections
  }
}

export function mergeWithLocalDefaults(
  remote: LandingContent | null,
  local: LandingContent = localLandingContent
): LandingContent {
  if (!remote) return local

  const plans: Record<string, PlanConfig> = { ...local.plans }
  for (const [id, remotePlan] of Object.entries(remote.plans)) {
    const localPlan = local.plans[id]
    plans[id] = localPlan ? mergePlan(localPlan, remotePlan) : remotePlan
  }

  const planOrder =
    remote.planOrder.filter((id) => plans[id]).length > 0 ? remote.planOrder.filter((id) => plans[id]) : local.planOrder

  return {
    contentVersion: remote.contentVersion || local.contentVersion,
    planOrder,
    plans,
    compareTitle: remote.compareTitle || local.compareTitle,
    compareSections: remote.compareSections?.length ? remote.compareSections : local.compareSections
  }
}

function getContentSourceFlag(): 'local' | 'crm' {
  const flag = import.meta.env.VITE_ELITE_PLANS_CONTENT_SOURCE
  return flag === 'crm' ? 'crm' : 'local'
}

/**
 * Loads landing content. CMS fetch is stubbed until a vendor exists;
 * local content is always the fallback.
 */
export async function loadElitePlansContent(): Promise<LandingContent> {
  if (getContentSourceFlag() === 'crm') {
    try {
      // Tomorrow: const payload = await fetchCrmElitePlansContent()
      const mapped = mapCrmLandingContent(null)
      return mergeWithLocalDefaults(mapped)
    } catch {
      return localElitePlansContentSource.load()
    }
  }

  return localElitePlansContentSource.load()
}

export function toCompareTitle(
  value: unknown,
  fallback: LandingContent['compareTitle']
): LandingContent['compareTitle'] {
  // CMS may still send a bare string; treat it as leading-only.
  if (typeof value === 'string' && value) {
    return { leading: value, connector: '', trailing: '' }
  }
  if (!isRecord(value)) return fallback
  if (typeof value.leading === 'string' && typeof value.connector === 'string' && typeof value.trailing === 'string') {
    return {
      leading: value.leading,
      connector: value.connector,
      trailing: value.trailing
    } satisfies CompareTitleParts
  }
  return fallback
}

export function toTestimonialsTitle(value: unknown, fallback: TestimonialsTitle): TestimonialsTitle {
  if (typeof value === 'string' && value) return { line1: value }
  if (!isRecord(value)) return fallback
  if (typeof value.line1 === 'string') {
    return {
      line1: value.line1,
      line2: typeof value.line2 === 'string' ? value.line2 : undefined
    }
  }
  return fallback
}
